import { Typography } from "@mui/material";
import { useContext } from "react";
import useStore from "../../../../Store/Store";
import { theme } from "../../../../Themes/Themes";
import { SimulationDescription } from "./SimulationDescription";
import SimulationsEditPanel from "./SimulationsEditPanel";
import { SimulationsContext } from "./SimulationsTab";
import {
  saveRecordedActions,
  startRecordingActions,
  discardRecordedActions,
} from "../../../../Store/UndoManager";
import { resetTemporaryStyles } from "../../../DiagramHighlight/DiagramHighlight";

export const SimulationsBody: React.FC = () => {
  let {
    isRunning,
    simulationNameError,
    isSettingSimulationName,
    currentUnsavedSimulationObject,
    setSimulationNameError,
    setIsSettingSimulationName,
    setIsNewSimulation,
  } = useContext(SimulationsContext);
  const {
    simulations,
    setSimulations,
    isEditingSimulations,
    setIsEditingSimulations,
    selectedSimulationKey,
    setSelectedSimulationKey,
    setCurrentUnsavedSimulationObject,
    setSelectedCell,
    isSimulationFullSizeMode,
  } = useStore((state) => ({
    simulations: state.simulationsState.simulations,
    setSimulations: state.setSimulations,
    isEditingSimulations: state.simulationsState.isEditingSimulations,
    setIsEditingSimulations: state.setIsEditingSimulations,
    selectedSimulationKey: state.simulationsState.selectedSimulationKey,
    setSelectedSimulationKey: state.setSelectedSimulationKey,
    setCurrentUnsavedSimulationObject: state.setCurrentUnsavedSimulationObject,
    setSelectedCell: state.setSelectedCell,
    isSimulationFullSizeMode: state.isSimulationFullSizeMode,
  }));

  const handleSimulationNameChange = async (e) => {
    await setCurrentUnsavedSimulationObject({
      ...currentUnsavedSimulationObject,
      name: e.target.value,
    });
    // check if name is already taken
    if (Object.keys(simulations).includes(e.target.value)) {
      setSimulationNameError("Name Already Taken!");
    } else {
      setSimulationNameError("");
    }
  };

  const handleCreateSimulation = async () => {
    if (currentUnsavedSimulationObject.name === "") {
      setSimulationNameError("Name cannot be empty!");
      return;
    }
    if (simulationNameError) {
      return;
    }

    const newSimulation: Simulation = {
      name: currentUnsavedSimulationObject.name,
      simSteps: [],
      simulationNodesAndEdges: {},
      description: "",
      isAIGenerated: false,
      keywords: "",
    };
    await startRecordingActions("SIMULATION_CREATED", {
      supressWarning: true,
    });
    await setCurrentUnsavedSimulationObject(newSimulation);
    await setSimulations({
      ...simulations,
      [currentUnsavedSimulationObject.name]: currentUnsavedSimulationObject,
    });
    setIsSettingSimulationName(false);
    await resetTemporaryStyles();
    await setSelectedSimulationKey(currentUnsavedSimulationObject.name);
    await setIsEditingSimulations("SimSteps");
    setIsNewSimulation(true);
    await saveRecordedActions("SIMULATION_CREATED");
    await startRecordingActions("SIMULATION_MODIFIED", {
      supressWarning: true,
    });
  };

  const handleCancelSimulation = async () => {
    setIsSettingSimulationName(false);
    setIsNewSimulation(false);
    setSimulationNameError("");
    await setCurrentUnsavedSimulationObject({
      name: "",
      simSteps: [],
      simulationNodesAndEdges: {},
      description: "",
      isAIGenerated: false,
      keywords: "",
    });
    await discardRecordedActions("CURRENT_UNSAVED_SIMULATION_OBJECT");
  };

  const handleKeyPress = async (e) => {
    if (e.key === "Enter") {
      await handleCreateSimulation();
    } else if (e.key === "Escape") {
      await handleCancelSimulation();
    }
  };

  // When setting a new simulation name
  if (isSettingSimulationName && !selectedSimulationKey) {
    return (
      <div className="simulationBody" style={{ position: "relative" }}>
        <input
          placeholder="Add New Simulation Name"
          onKeyPress={handleKeyPress}
          onChange={handleSimulationNameChange}
          value={currentUnsavedSimulationObject.name}
          className="simulationNameInput"
          autoFocus
          style={{
            marginBottom: "20px",
          }}
        />
        {simulationNameError && (
          <Typography
            variant="subtitle2"
            color={theme.custom.pinkerPink}
            style={{ marginBottom: "15px" }}
          >
            {simulationNameError}
          </Typography>
        )}
        <div
          className="DialogButtonGroupHorizontal"
          style={{
            paddingBottom: "10px",
            justifyContent: "center",
            gap: "20px",
          }}
        >
          <div
            className="Button DialogButton"
            onClick={handleCancelSimulation}
            style={{ marginRight: "10px" }}
          >
            <Typography
              mx={0.2}
              my={0.8}
              fontSize="15px"
              fontWeight="Normal"
              color="#FFAEA6"
            >
              Cancel
            </Typography>
          </div>
          <div
            className="Button DialogButton"
            onClick={handleCreateSimulation}
            style={{ marginLeft: "10px" }}
          >
            <Typography
              mx={0.2}
              my={0.8}
              fontSize="15px"
              fontWeight="Normal"
              color="#FFAEA6"
            >
              Create
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  // When a simulation is selected
  if (selectedSimulationKey) {
    const bodyClassName = isSimulationFullSizeMode
      ? "simulationBody full-size-mode"
      : "simulationBody";

    return (
      <div
        className={bodyClassName}
        style={{
          position: "relative",
          display: "flex",
          minHeight: "auto",
          height: "100%",
          width: "100%",
          overflow: "visible", // Changed from hidden to visible to show borders
          boxSizing: "border-box",
        }}
      >
        {(!isEditingSimulations || isEditingSimulations === "SimSteps") && (
          <SimulationsEditPanel />
        )}
        {((!isEditingSimulations && !isRunning) ||
          isEditingSimulations === "Description") && <SimulationDescription />}
      </div>
    );
  }

  // Default: return null when no simulation is selected and not setting name
  return null;
};
